import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useEmoji } from '@/composables/useEmoji';
import { useReactionsStore, ReactionTimelineEvent } from '../store/useReactionsStore';
import { bucketByVideoTime, bucketCountForWidth, useVideoTimeline } from './useVideoTimeline';

export type { ReactionTimelineEvent };

export interface ReactionTimelineMarkerReaction {
  emoji: string;
  count: number;
}

export interface ReactionTimelineMarker {
  bucketIndex: number;
  leftPercent: number;
  reactions: ReactionTimelineMarkerReaction[];
  seekTime: number;
}

/**
 * Buckets timestamped reaction events across the video and breaks each bucket
 * down by emoji. Exported standalone so the grouping can be unit tested without
 * mounting the composable's store/DOM wiring.
 */
export const bucketReactionEvents = (
  events: ReactionTimelineEvent[],
  duration: number,
  findByName: (name: string) => string,
  bucketCount?: number
): ReactionTimelineMarker[] => {
  // Guards against stale reaction events whose name no longer resolves to a
  // displayable emoji (e.g. a reaction type that's since been retired).
  const resolvableEvents = events.filter(event => findByName(event.name));

  return bucketByVideoTime(resolvableEvents, duration, bucketCount).map(bucket => {
    const countsByName = new Map<string, number>();
    const lastSeenIndex = new Map<string, number>();
    bucket.events.forEach((event, eventIndex) => {
      countsByName.set(event.name, (countsByName.get(event.name) || 0) + 1);
      lastSeenIndex.set(event.name, eventIndex);
    });

    // Most-frequent reaction first; ties go to whichever was added most recently.
    const reactions = Array.from(countsByName.entries())
      .sort((a, b) => {
        if (b[1] !== a[1]) {
          return b[1] - a[1];
        }

        return (lastSeenIndex.get(b[0]) ?? 0) - (lastSeenIndex.get(a[0]) ?? 0);
      })
      .map(([name, count]) => ({ emoji: findByName(name) || '', count }));

    return {
      bucketIndex: bucket.bucketIndex,
      leftPercent: bucket.leftPercent,
      reactions,
      seekTime: bucket.seekTime,
    };
  });
};

export const useReactionTimeline = () => {
  const { reactionEvents } = storeToRefs(useReactionsStore());
  const { findByName } = useEmoji();
  const { containerWidth, duration, progressHolder, seekTo } = useVideoTimeline();

  const markers = computed((): ReactionTimelineMarker[] =>
    bucketReactionEvents(reactionEvents.value, duration.value, findByName, bucketCountForWidth(containerWidth.value))
  );

  return { markers, progressHolder, seekTo };
};
